//////////////////////////////////////////////////////////////////////////////
//
//  useSignInStore - 로그인 상태 관리
//
//  작성자: MediInStep Team
//  생성일: 2025-12-04
//
//  설명: Zustand 기반 로그인 Store
//
//////////////////////////////////////////////////////////////////////////////

import { create } from "zustand"
import { request } from "app/network"
import { storeUserCredential } from "Shared/Utils/CredentialStorage"
import { ILoginInfo } from "Screens/Authentication/sign-in/interfaces/ILoginInfo"

interface SignInStoreState {
  /**
   * 사용자 ID
   */
  userId: string

  /**
   * 비밀번호
   */
  password: string

  /**
   * 로그인 상태
   */
  isSignedIn: boolean

  /**
   * 요청 파라미터
   */
  paramz: any

  /**
   * 사용자 ID 설정
   */
  setUserId: (userId: string) => void

  /**
   * 비밀번호 설정
   */
  setPassword: (password: string) => void

  /**
   * 로그인 상태 설정
   */
  setIsSignedIn: (signedIn: boolean) => void

  /**
   * 로그인 요청
   */
  login: (failCallback: (isFail: boolean, errorMessage?: string) => void) => Promise<void>

  /**
   * 리셋
   */
  reset: () => void
}

/**
 * 로그인 스토어
 */
export const useSignInStore = create<SignInStoreState>((set, get) => ({
  userId: "",
  password: "",
  isSignedIn: false,
  paramz: {},

  setUserId: (userId: string) => {
    set({ userId })
  },

  setPassword: (password: string) => {
    set({ password })
  },

  setIsSignedIn: (signedIn: boolean) => {
    set({ isSignedIn: signedIn })
  },

  login: async (failCallback: (isFail: boolean, errorMessage?: string) => void) => {
    const { userId, password } = get()
    const param = {
      Id: userId, // API 요청 형식에 맞게 대문자 I 사용
      Password: password,
      GrantType: "password",
    }

    set({ paramz: param })

    try {
      const res = await request("login", { paramz: param })
      const userInfo: ILoginInfo = res.data as ILoginInfo

      // token이 있으면 성공으로 간주
      if (userInfo && userInfo.token) {
        await storeUserCredential(userInfo)
        set({ isSignedIn: true })

        // JWT 토큰 페이로드 출력
        try {
          const payload = JSON.parse(atob(userInfo.token.split('.')[1]))
          console.log("=".repeat(80))
          console.log("🔐 JWT TOKEN PAYLOAD (로그인 성공):")
          console.log("=".repeat(80))
          console.log("Full Payload:", payload)
          console.log("-".repeat(80))
          console.log("📋 Key Claims:")
          console.log("  • userId:", payload.userId || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"])
          console.log("  • tenantSeqId:", payload.tenantSeqId)
          console.log("  • role:", payload.role || payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"])
          console.log("  • isMasterUser:", payload.isMasterUser)
          console.log("=".repeat(80))
        } catch (error) {
          console.error("Failed to decode JWT token:", error)
        }

        failCallback(false)
      } else {
        failCallback(true, "로그인 응답이 올바르지 않습니다.")
      }
    } catch (error: any) {
      console.error("Login error:", error)

      // 에러 메시지 추출
      let errorMessage = "아이디 또는 비밀번호가 올바르지 않습니다."

      if (error.response) {
        const status = error.response.status
        const data = error.response.data

        if (status === 401) {
          // 401 Unauthorized - 인증 실패
          errorMessage = data?.message || data?.error || "아이디 또는 비밀번호가 올바르지 않습니다."
        } else if (status === 403) {
          // 403 Forbidden - 권한 없음
          errorMessage = data?.message || data?.error || "접근 권한이 없습니다."
        } else if (status >= 500) {
          // 서버 에러
          errorMessage = "서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요."
        } else if (data?.message) {
          // 기타 에러 메시지
          errorMessage = data.message
        }
      } else if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        errorMessage = "네트워크 연결을 확인해주세요."
      }

      failCallback(true, errorMessage)
    }
  },

  reset: () => {
    set({
      userId: "",
      password: "",
      isSignedIn: false,
      paramz: {},
    })
  },
}))
